<script lang="ts" setup>
import { onMounted, ref, shallowRef } from "vue";
import { GoogleMap, MarkerCluster } from "vue3-google-map";
import type { MarkerClustererOptions } from "@googlemaps/markerclusterer";
import { SuperClusterViewportAlgorithm } from "@googlemaps/markerclusterer";
import type { TruckDto } from "@/api/trucks/dto/truck.dto";
import { API } from "@/api/api";
import { useError } from "@/composables/useError";
import { useGoogleMap } from "@/composables/useGoogleMap";
import ContainerLayout from "@/components/layout/ContainerLayout.vue";
import { useI18n } from "vue-i18n";
import { USA_MAP_CENTER } from "@/constants/map";
import TruckMarker from "@/views/fleets/map/components/TruckMarker.vue";
import { usePaginatedLoader } from "@/composables/reactive/usePaginatedLoader";
import LoadingIcon from "@/components/icons/LoadingIcon.vue";

const { showAPIError } = useError();
const { getApiKey, getMapId } = useGoogleMap();
const { t } = useI18n();
const { loadAll, isLoading: loadingAllTrucks } = usePaginatedLoader();

const api = new API();

const mapRef = ref<InstanceType<typeof GoogleMap> | null>(null);
const mapCenter = ref(USA_MAP_CENTER);
const trucksWithCoords = shallowRef<TruckDto[]>([]);

const algorithmOptions = {
  radius: 200,
  maxZoom: 10,
  minPoints: 4,
  viewportPadding: 0,
};

const clusterOptions: MarkerClustererOptions = {
  algorithm: new SuperClusterViewportAlgorithm(algorithmOptions),
};

async function getAllTrucks() {
  try {
    await loadAll<TruckDto>(
      async (offset, pageSize) => {
        const page = Math.floor(offset / pageSize) + 1;
        const response = await api.trucks.findTrucks({ page, pageSize }, false);

        if (response.errorData) {
          showAPIError(response);
          return null;
        }

        return response?.data?.items ?? [];
      },
      {
        pageSize: 300,
        onBatch: (items) => {
          const withCoords = items.filter((t) => t.currentLocation?.coordinates);
          if (!withCoords.length) {
            return;
          }

          trucksWithCoords.value = [...trucksWithCoords.value, ...withCoords];
        },
      },
    );
  } catch (error) {
    console.error(error);
  }
}

onMounted(async () => {
  await getAllTrucks();
});
</script>

<template>
  <container-layout :header="t('trucks.live-map')">
    <div class="trucks-map">
      <google-map
        ref="mapRef"
        :api-key="getApiKey()"
        :center="mapCenter"
        :disable-default-ui="true"
        :map-id="getMapId()"
        :zoom="4"
        style="width: 100%; height: 100%"
      >
        <marker-cluster :options="clusterOptions" :render-debounce-delay="100">
          <truck-marker v-for="truck in trucksWithCoords" :key="truck.id" :truck="truck" />
        </marker-cluster>
      </google-map>

      <div v-if="loadingAllTrucks" class="trucks-map__loader">
        <div class="d-flex align-center justify-center col-gap-3">
          <loading-icon />

          <span>{{ t("trucks.loading-trucks-facilities") }}</span>
        </div>
      </div>
    </div>
  </container-layout>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.trucks-map {
  position: relative;
  height: 100%;

  &__loader {
    padding: 12px;
    background-color: $white;
    position: absolute;
    bottom: 30px;
    right: 30px;
    z-index: 1;
    border: 1px solid $primary;
    border-radius: 8px;
    color: $primary;
  }
}
</style>
